import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';
import { useNavigateSearch } from 'hooks/useNavigateSearch';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { Breakpoints } from 'utils/const.styles';

import { faCheck } from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/ProgressWizard.styled';

/**
 * @author @buunsal
 * @description This component is used to display the progress of the wizard and handles the step state
 * @param {Object} props
 * @param {Object[]} props.stages - Array of stages
 * @param {Function} props.setIndex - Function to set index of current stage
 * @param {Number} [props.clickable = true] - If true, the steps are clickable
 * @param {Boolean} [props.paramMode = false] - If true, progress is controlled by url params
 * @returns {JSX.Element} ProgressWizard component
 * @example 
 * <ProgressWizard
	stages={[
	{ name: 'Customer Details', icon: faUserTag },
	{ name: 'Order Details', icon: faBoxCheck }
	]}
	index={tabIndex}
	setIndex={()=>setTabIndex(1)}
	/>
 *
 */
const ProgressWizard = (props) => {
	const {
		stages,
		index,
		setIndex,
		className,
		clickable = true,
		paramMode = false,
		gutterBottom,
		showDescription
	} = props;

	const { setStep } = useProgressWizard();

	const [currentIndex, setCurrentIndex] = useState(index ?? 0);

	const [searchParams] = useSearchParams();
	const wizardStep = searchParams?.get('wizardStep');

	useEffect(() => {
		if (paramMode && wizardStep) {
			const index = Number(wizardStep) - 1;
			setCurrentIndex(index);
			setIndex(index);
		}
	}, [wizardStep]);

	useUpdateEffect(() => {
		setCurrentIndex(index);
	}, [index]);

	const tabletView = useMediaQuery(Breakpoints.tablet);

	const formatPageNumber = (num) => (num < 10 ? `0${num}` : num);

	return (
		<S.Container className={`progressWizard ${gutterBottom ? 'gutterBottom' : ''} ${className ?? ''}`}>
			{!tabletView ? (
				stages?.map((stage, i) => (
					<S.Stage key={i} className={`step-${i}`} clickable={clickable}>
						<S.IconContainer
							onClick={() => {
								clickable && (paramMode ? setStep(i + 1) : setIndex(i));
							}}
							className={
								(currentIndex === i && stage.success) || currentIndex > i
									? 'completed'
									: currentIndex === i
										? 'active'
										: ''
							}>
							{(currentIndex === i && stage.success) || currentIndex > i ? (
								<Icon icon={faCheck} size="medium" />
							) : (
								<Typography
									fontWeight="500"
									color={currentIndex < i ? 'var(--content-4)' : 'var(--alwaysWhiteColor)'}>
									{i + 1}
								</Typography>
							)}
						</S.IconContainer>
						<Typography
							onClick={() => {
								clickable && (paramMode ? setStep(i + 1) : setIndex(i));
							}}
							css={S.LabelText}
							fontWeight={currentIndex === i ? '700' : '500'}
							color={currentIndex < i ? 'var(--content-4)' : 'var(--black)'}>
							{stage?.name}
						</Typography>
					</S.Stage>
				))
			) : (
				<S.MobileOuterDiv className="wizardMobile">
					<S.StageMobile>
						<Typography noWrap variant="p2" fontWeight="500" color="var(--black)" className="name">
							{stages && stages[index]?.name}
						</Typography>

						<S.MobilePageNumber>
							<Typography variant="p2" className="active" fontWeight="800" color="var(--black)">
								{formatPageNumber(index + 1)}
							</Typography>
							<Typography variant="p2" className="active" fontWeight="500" color="var(--content-4)">
								/{formatPageNumber(stages?.length)}
							</Typography>
						</S.MobilePageNumber>
					</S.StageMobile>
					{showDescription && stages && stages[index]?.description && (
						<Typography noWrap variant="caption" color="var(--content-3)" className="description">
							{stages[index].description}
						</Typography>
					)}
					<div style={{ display: 'flex', gap: '1rem' }}>
						{stages?.map((stage, i) => (
							<S.DivContainer
								key={i}
								className={
									(currentIndex === i && stage.success) || currentIndex > i
										? 'completed'
										: currentIndex === i
											? 'active'
											: ''
								}
							/>
						))}
					</div>
				</S.MobileOuterDiv>
			)}
		</S.Container>
	);
};

export const useProgressWizard = () => {
	const navigateSearch = useNavigateSearch();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const wizardStep = searchParams?.get('wizardStep') ? Number(searchParams?.get('wizardStep')) : 1;

	const nextStep = useCallback(() => {
		const params = {};

		searchParams.forEach((value, key) => {
			if (key !== 'wizardStep') {
				params[key] = value;
			}
		});

		navigateSearch(pathname, {
			...params,
			wizardStep: wizardStep + 1
		});
	}, [searchParams, wizardStep]);

	const prevStep = useCallback(() => {
		const params = {};

		searchParams.forEach((value, key) => {
			if (key !== 'wizardStep') {
				params[key] = value;
			}
		});

		navigateSearch(pathname, {
			...params,
			wizardStep: wizardStep - 1
		});
	}, [searchParams, wizardStep]);

	const setStep = useCallback(
		(step) => {
			const params = {};

			searchParams.forEach((value, key) => {
				if (key !== 'wizardStep') {
					params[key] = value;
				}
			});

			navigateSearch(pathname, {
				...params,
				wizardStep: step
			});
		},
		[searchParams]
	);

	return { nextStep, prevStep, setStep };
};

export default ProgressWizard;
